export type DeepSkyObject = {
    id: string
    slug: string
    name: string
    designation: string
    type: string
    constellation: string
    distance: string
    magnitude: string
    /** J2000 right ascension in hours. */
    ra: number
    /** J2000 declination in degrees. */
    dec: number
    sentence: string
    description: string
    finding: string
    season: string
    instrument: string
    image: string | null
    procedural?: 'double-star' | 'pleiades' | 'double-cluster'
    credit: string
    /** Where it hides in the eyepiece field: fractions of width and height, and size as a fraction of width. */
    field: { x: number, y: number, size: number }
}

export const DEEP_SKY_OBJECTS: DeepSkyObject[] = [
    {
        id: 'm51',
        slug: 'whirlpool-galaxy',
        name: 'Whirlpool Galaxy',
        designation: 'M51 · NGC 5194',
        type: 'Spiral galaxy',
        constellation: 'Canes Venatici',
        distance: '31 million light-years',
        magnitude: '8.4',
        ra: 13.498,
        dec: 47.195,
        sentence: 'A grand spiral galaxy in the middle of swallowing a smaller companion.',
        description: 'The first galaxy in which anyone saw spiral arms, sketched by Lord Rosse in 1845. Its companion, NGC 5195, is tugging on one arm, and the encounter has set off waves of star formation that show as pink knots along the spirals.',
        finding: 'Start at Alkaid, the last star in the handle of the Plough, and sweep about three and a half degrees south-west. In our 40 cm reflector the two cores and a hint of the arms show on a dark night.',
        season: 'Spring evenings, high overhead',
        instrument: 'Best in the 40 cm reflector',
        image: '/images/sky/m51-whirlpool.jpg',
        credit: 'NASA, ESA, S. Beckwith (STScI) and the Hubble Heritage Team',
        field: { x: 0.07, y: 0.3, size: 0.13 }
    },
    {
        id: 'm45',
        slug: 'pleiades',
        name: 'The Pleiades',
        designation: 'M45 · Seven Sisters',
        type: 'Open star cluster',
        constellation: 'Taurus',
        distance: '444 light-years',
        magnitude: '1.6',
        ra: 3.79,
        dec: 24.117,
        sentence: 'Hot young blue stars, a hundred million years old, drifting through a veil of dust.',
        description: 'Most people see six or seven stars with the naked eye; binoculars show dozens. The blue haze photographs pick up is not left over from the stars’ birth but an unrelated cloud of dust the cluster happens to be passing through.',
        finding: 'Follow the line of Orion’s belt up and to the right, past orange Aldebaran, to a small misty dipper of stars. Binoculars give the best view; it is too large for most telescopes.',
        season: 'Autumn to early spring',
        instrument: 'Best in binoculars or the 80 mm refractor',
        image: null,
        procedural: 'pleiades',
        credit: 'Drawn at CosmicLens from the cluster’s real star positions',
        field: { x: 0.17, y: 0.72, size: 0.14 }
    },
    {
        id: 'm13',
        slug: 'hercules-cluster',
        name: 'Great Hercules Cluster',
        designation: 'M13 · NGC 6205',
        type: 'Globular cluster',
        constellation: 'Hercules',
        distance: '25,000 light-years',
        magnitude: '5.8',
        ra: 16.695,
        dec: 36.46,
        sentence: 'Several hundred thousand stars packed into a ball 145 light-years wide.',
        description: 'Globular clusters are among the oldest things in the galaxy, their stars billions of years older than the Sun. In 1974 the Arecibo radio telescope beamed a message toward M13; it will arrive in about 25,000 years.',
        finding: 'Find the Keystone, the lopsided square at the centre of Hercules, and look a third of the way down its western side. A telescope resolves the edges into a swarm of separate stars.',
        season: 'Summer, high in the evening sky',
        instrument: 'Best in the 40 cm reflector',
        image: '/images/sky/m13-hercules.jpg',
        credit: 'NASA, ESA and the Hubble Heritage Team (STScI/AURA)',
        field: { x: 0.27, y: 0.25, size: 0.11 }
    },
    {
        id: 'albireo',
        slug: 'albireo',
        name: 'Albireo',
        designation: 'Beta Cygni',
        type: 'Double star',
        constellation: 'Cygnus',
        distance: 'about 430 light-years',
        magnitude: '3.1 and 5.1',
        ra: 19.512,
        dec: 27.96,
        sentence: 'A gold star and a sapphire one side by side: the finest colour contrast in the northern sky.',
        description: 'To the eye Albireo is a single star at the head of the swan. Any small telescope splits it into a warm golden giant and a hot blue-white companion. Whether they truly orbit each other is still argued over.',
        finding: 'Albireo marks the beak of Cygnus, at the foot of the Northern Cross, halfway between Vega and Altair. Use low power: the pair is wide and the colours show best slightly out of focus.',
        season: 'Summer and early autumn',
        instrument: 'Best in the 80 mm refractor',
        image: null,
        procedural: 'double-star',
        credit: 'Drawn at CosmicLens from the stars’ true colours and separation',
        field: { x: 0.36, y: 0.62, size: 0.07 }
    },
    {
        id: 'm42',
        slug: 'orion-nebula',
        name: 'Orion Nebula',
        designation: 'M42 · NGC 1976',
        type: 'Emission nebula',
        constellation: 'Orion',
        distance: '1,344 light-years',
        magnitude: '4.0',
        ra: 5.588,
        dec: -5.391,
        sentence: 'A stellar nursery where new stars are switching on inside a cloud of glowing gas.',
        description: 'The nearest large region of star birth to Earth. At its heart four young stars, the Trapezium, light the gas around them. Telescopes show the cloud in grey-green; long exposures reveal the reds and pinks of glowing hydrogen.',
        finding: 'Below Orion’s belt hangs his sword, three fainter stars in a line. The middle one is not a star at all: even the naked eye sees it as a soft smudge.',
        season: 'Winter evenings, low in the south',
        instrument: 'Glorious in any telescope',
        image: '/images/sky/m42-orion.jpg',
        credit: 'NASA, ESA, M. Robberto (STScI/ESA) and the Hubble Space Telescope Orion Treasury Project Team',
        field: { x: 0.45, y: 0.24, size: 0.14 }
    },
    {
        id: 'm57',
        slug: 'ring-nebula',
        name: 'Ring Nebula',
        designation: 'M57 · NGC 6720',
        type: 'Planetary nebula',
        constellation: 'Lyra',
        distance: '2,600 light-years',
        magnitude: '8.8',
        ra: 18.893,
        dec: 33.029,
        sentence: 'The cast-off shell of a dying Sun-like star, lit by the white dwarf at its centre.',
        description: 'About four thousand years ago the star at the centre shrugged off its outer layers. We are looking almost straight down a tube of glowing gas, which is why it appears as a ring. Our Sun will do something similar in five billion years.',
        finding: 'Halfway between the two stars at the bottom of the small parallelogram below Vega. In the eyepiece it looks like a tiny grey smoke ring, unmistakable once found.',
        season: 'Summer, high overhead',
        instrument: 'Best in the 40 cm reflector',
        image: '/images/sky/m57-ring.jpg',
        credit: 'NASA, ESA and the Hubble Heritage Team (STScI/AURA)',
        field: { x: 0.53, y: 0.75, size: 0.08 }
    },
    {
        id: 'm81',
        slug: 'bodes-galaxy',
        name: 'Bode’s Galaxy',
        designation: 'M81 · NGC 3031',
        type: 'Spiral galaxy',
        constellation: 'Ursa Major',
        distance: '11.8 million light-years',
        magnitude: '6.9',
        ra: 9.926,
        dec: 69.065,
        sentence: 'A bright spiral, its arms traced by young blue star clusters, visible in binoculars.',
        description: 'One of the brightest galaxies in the sky and a neighbour of M82, whose disturbed shape it is partly responsible for. A dark night shows its bright core and an oval glow; a large telescope hints at the arms.',
        finding: 'Draw a line from Phecda through Dubhe in the Plough and carry on the same distance again. M81 and M82 sit together in one low-power field.',
        season: 'All year, best in spring',
        instrument: 'Best in the 40 cm reflector',
        image: '/images/sky/m81-bode.jpg',
        credit: 'NASA, ESA and the Hubble Heritage Team (STScI/AURA)',
        field: { x: 0.61, y: 0.33, size: 0.12 }
    },
    {
        id: 'm1',
        slug: 'crab-nebula',
        name: 'Crab Nebula',
        designation: 'M1 · NGC 1952',
        type: 'Supernova remnant',
        constellation: 'Taurus',
        distance: '6,500 light-years',
        magnitude: '8.4',
        ra: 5.575,
        dec: 22.014,
        sentence: 'The wreckage of a star that exploded in 1054, seen then as a guest star bright enough to show by day.',
        description: 'Chinese astronomers recorded the explosion in July 1054. At the centre spins a pulsar, the collapsed core of the star, turning thirty times a second and still powering the glow of the expanding debris.',
        finding: 'Just over a degree north-west of Zeta Tauri, the tip of the bull’s southern horn. It shows as a faint oval patch; a dark sky and patience help.',
        season: 'Winter',
        instrument: 'Best in the 40 cm reflector',
        image: '/images/sky/m1-crab.jpg',
        credit: 'NASA, ESA, J. Hester and A. Loll (Arizona State University)',
        field: { x: 0.69, y: 0.72, size: 0.1 }
    },
    {
        id: 'm82',
        slug: 'cigar-galaxy',
        name: 'Cigar Galaxy',
        designation: 'M82 · NGC 3034',
        type: 'Starburst galaxy',
        constellation: 'Ursa Major',
        distance: '12 million light-years',
        magnitude: '8.4',
        ra: 9.931,
        dec: 69.68,
        sentence: 'A galaxy blazing with new stars, blowing plumes of hot gas out of its own disc.',
        description: 'A close pass by M81 stirred up M82’s gas, and it is now forming stars ten times faster than our galaxy. Seen edge on, it looks like a thin silver splinter crossed by dark lanes of dust.',
        finding: 'In the same low-power field as M81, about half a degree to the north.',
        season: 'All year, best in spring',
        instrument: 'Best in the 40 cm reflector',
        image: '/images/sky/m82-cigar.jpg',
        credit: 'NASA, ESA and the Hubble Heritage Team (STScI/AURA)',
        field: { x: 0.77, y: 0.22, size: 0.1 }
    },
    {
        id: 'double-cluster',
        slug: 'double-cluster',
        name: 'Double Cluster',
        designation: 'NGC 869 and NGC 884',
        type: 'Pair of open clusters',
        constellation: 'Perseus',
        distance: '7,500 light-years',
        magnitude: '3.7 and 3.8',
        ra: 2.333,
        dec: 57.13,
        sentence: 'Two young star clusters side by side, a few hundred suns each, in one low-power view.',
        description: 'Visible to the naked eye as a brighter knot in the Milky Way between Perseus and Cassiopeia. Through a telescope the two swarms glitter side by side, with a few red supergiants among the blue-white stars.',
        finding: 'Follow the line from the two brightest stars in the W of Cassiopeia toward Perseus. On a dark night it is a misty patch even without binoculars.',
        season: 'Autumn and winter',
        instrument: 'Best in the 80 mm refractor',
        image: null,
        procedural: 'double-cluster',
        credit: 'Drawn at CosmicLens from the clusters’ star counts',
        field: { x: 0.84, y: 0.62, size: 0.11 }
    },
    {
        id: 'm27',
        slug: 'dumbbell-nebula',
        name: 'Dumbbell Nebula',
        designation: 'M27 · NGC 6853',
        type: 'Planetary nebula',
        constellation: 'Vulpecula',
        distance: '1,360 light-years',
        magnitude: '7.5',
        ra: 19.993,
        dec: 22.721,
        sentence: 'The first planetary nebula ever found: an hourglass of gas puffed out by an ageing star.',
        description: 'Charles Messier found it in 1764 while hunting comets. It is bright enough for binoculars, and the apple-core shape shows clearly in any telescope on a dark night.',
        finding: 'From the arrow of Sagitta, hop three degrees north of its tip, Gamma Sagittae.',
        season: 'Late summer and autumn',
        instrument: 'Best in the 40 cm reflector',
        image: '/images/sky/m27-dumbbell.jpg',
        credit: 'European Southern Observatory',
        field: { x: 0.92, y: 0.3, size: 0.09 }
    },
    {
        id: 'm16',
        slug: 'eagle-nebula',
        name: 'Pillars of Creation',
        designation: 'M16 · Eagle Nebula',
        type: 'Star-forming region',
        constellation: 'Serpens',
        distance: '7,000 light-years',
        magnitude: '6.0',
        ra: 18.313,
        dec: -13.817,
        sentence: 'Towers of cold gas and dust, light-years tall, with new stars forming inside them.',
        description: 'The most famous picture Hubble ever took. Ultraviolet light from nearby young stars is slowly eroding the pillars; dense knots at their tips are where new stars are condensing.',
        finding: 'Low in our southern summer sky, about two and a half degrees north-west of the Swan Nebula in Sagittarius. The cluster is easy; the pillars need a large telescope and a filter.',
        season: 'Summer, low in the south',
        instrument: 'Best in the 40 cm reflector with a nebula filter',
        image: '/images/sky/m16-pillars.jpg',
        credit: 'NASA, ESA and the Hubble Heritage Team (STScI/AURA)',
        field: { x: 0.94, y: 0.8, size: 0.1 }
    }
]

export const objectBySlug = (slug: string): DeepSkyObject | undefined => DEEP_SKY_OBJECTS.find(object => object.slug === slug)
